// Grosszuegige Drag&Drop-Dropzone fuer den AB-Upload (Muster: Drag&Drop-Konventionen aus
// materialErfassung/MaterialAttachments.js — window-Guard gegen Browser-Standardverhalten,
// eigener dragOver-State). Nur PDF, ein Dokument je Upload (der Server verarbeitet synchron
// und liefert Status + Zeilenzahl zurueck) -> danach direkter Sprung in die Detailansicht.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Upload, FileScan, Loader2 } from 'lucide-react';
import { btnGhost } from './ui';
import { uploadDocument } from '../api';

const MAX_SIZE_BYTES = 20 * 1024 * 1024;

const AbUpload = ({ user, isDarkMode, t, onUploaded, showSuccess, showError }) => {
  const [dragOver, setDragOver] = useState(false);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  // Verhindert, dass ein NEBEN die Dropzone gezogenes PDF den Browser navigiert.
  useEffect(() => {
    const prevent = (e) => { e.preventDefault(); };
    window.addEventListener('dragover', prevent);
    window.addEventListener('drop', prevent);
    return () => { window.removeEventListener('dragover', prevent); window.removeEventListener('drop', prevent); };
  }, []);

  const doUpload = useCallback(async (file) => {
    if (!file) return;
    if (file.type !== 'application/pdf' && !/\.pdf$/i.test(file.name || '')) {
      showError?.(t('ab.uploadOnlyPdf'));
      return;
    }
    if (file.size > MAX_SIZE_BYTES) {
      showError?.(t('ab.uploadTooLarge'));
      return;
    }
    setBusy(true);
    try {
      const res = await uploadDocument(user, file);
      showSuccess?.(t('ab.uploadOk', { n: res.lineCount ?? 0 }));
      onUploaded?.(res.id);
    } catch (e) {
      showError?.(`${t('ab.uploadError')}: ${e.message}`);
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  }, [user, t, onUploaded, showSuccess, showError]);

  const onPick = (e) => doUpload(e.target.files?.[0]);
  const onDragOver = (e) => { e.preventDefault(); if (!busy) setDragOver(true); };
  const onDragLeave = (e) => { e.preventDefault(); setDragOver(false); };
  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (!busy) doUpload(e.dataTransfer?.files?.[0]);
  };

  return (
    <div
      onDragOver={onDragOver}
      onDragEnter={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      onClick={() => !busy && fileRef.current?.click()}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && !busy) fileRef.current?.click(); }}
      className={`rounded-3xl border-2 border-dashed p-10 sm:p-14 text-center cursor-pointer transition-all ${busy ? 'cursor-wait' : ''} ${
        dragOver
          ? (isDarkMode ? 'border-indigo-400 bg-indigo-500/10' : 'border-indigo-400 bg-indigo-50')
          : (isDarkMode ? 'border-white/15 bg-white/[0.03] hover:border-white/25' : 'border-slate-300 bg-slate-50/60 hover:border-slate-400')
      }`}
    >
      <input ref={fileRef} type="file" accept="application/pdf,.pdf" className="hidden" onChange={onPick} disabled={busy} />
      <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4 ${
        isDarkMode ? 'bg-indigo-500/15 border border-indigo-400/20' : 'bg-indigo-100'
      }`}>
        {busy
          ? <Loader2 className={`w-7 h-7 animate-spin ${isDarkMode ? 'text-indigo-300' : 'text-indigo-600'}`} />
          : <FileScan className={`w-7 h-7 ${isDarkMode ? 'text-indigo-300' : 'text-indigo-600'}`} />}
      </div>
      <p className={`text-base font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
        {busy ? t('ab.uploadBusy') : t('ab.uploadDropzoneTitle')}
      </p>
      <p className={`text-sm mt-1 ${isDarkMode ? 'text-white/50' : 'text-slate-500'}`}>
        {t('ab.uploadDropzoneHint')}
      </p>
      <button
        type="button"
        disabled={busy}
        onClick={(e) => { e.stopPropagation(); fileRef.current?.click(); }}
        className={`${btnGhost(isDarkMode)} mt-5`}
      >
        <span className="flex items-center gap-2"><Upload className="w-4 h-4" />{t('ab.uploadButton')}</span>
      </button>
    </div>
  );
};

export default AbUpload;
